import type { RfqDto, RfqLineDto, SupplierRfqInboxItemDto, SupplierRfqRejectionReasonCode } from '../api/types'

export type RejectReasonOption = {
  code: SupplierRfqRejectionReasonCode
  label: string
}

export const REJECT_REASONS: RejectReasonOption[] = [
  { code: 'out_of_stock', label: 'Нет в наличии' },
  { code: 'no_delivery_to_address', label: 'Не доставляем по адресу' },
  { code: 'min_order_not_met', label: 'Минимальная сумма заказа' },
  { code: 'discontinued', label: 'Товар снят с ассортимента' },
  { code: 'other', label: 'Другое' },
]

const ARCHIVED_RFQ_STATUSES = new Set(['closed', 'cancelled'])
const ARCHIVED_SUPPLIER_DECISIONS = new Set(['rejected', 'completed', 'cancelled'])
const ARCHIVED_RESTAURANT_DECISIONS = new Set(['rejected', 'completed', 'cancelled'])

export const formatStatus = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'Открыта'
    case 'draft':
      return 'Черновик'
    case 'closed':
      return 'Закрыта'
    case 'pending':
      return 'Ожидает решения'
    case 'accepted':
      return 'В работе'
    case 'rejected':
      return 'Отклонена'
    case 'awaiting_customer_confirmation':
      return 'Ждёт подтверждения'
    case 'disputed':
      return 'Есть замечания'
    case 'completed':
      return 'Выполнена'
    case 'substitution_proposed':
      return 'Предложена замена'
    case 'cancelled':
      return 'Отменена'
    default:
      return status
  }
}

export const formatRejectionReason = (code: string | null | undefined): string | null => {
  if (!code) {
    return null
  }
  const match = REJECT_REASONS.find((reason) => reason.code === code)
  return match?.label ?? code
}

export const statusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'open':
    case 'accepted':
    case 'completed':
      return 'echat-badge echat-badge--success'
    case 'rejected':
    case 'closed':
      return 'echat-badge echat-badge--danger'
    case 'awaiting_customer_confirmation':
    case 'disputed':
    case 'substitution_proposed':
      return 'echat-badge echat-badge--warning'
    case 'pending':
    case 'draft':
    default:
      return 'echat-badge echat-badge--muted'
  }
}

export const supplierDecisionStatus = (item: SupplierRfqInboxItemDto): string =>
  (item.decisionStatus ?? 'pending').toLowerCase()

export const supplierRfqStatus = (item: SupplierRfqInboxItemDto): string =>
  (item.rfqStatus ?? 'open').toLowerCase()

export const restaurantSupplierDecision = (rfq: RfqDto): string =>
  (rfq.supplierDecisionStatus ?? '').toLowerCase()

export const isArchivedSupplierInbox = (item: SupplierRfqInboxItemDto): boolean => {
  const decision = supplierDecisionStatus(item)
  const rfqStatus = supplierRfqStatus(item)
  if (ARCHIVED_SUPPLIER_DECISIONS.has(decision)) {
    return true
  }
  if (ARCHIVED_RFQ_STATUSES.has(rfqStatus)) {
    return true
  }
  return false
}

export const isArchivedRestaurantRfq = (rfq: RfqDto): boolean => {
  const status = rfq.status.toLowerCase()
  if (ARCHIVED_RFQ_STATUSES.has(status)) {
    return true
  }
  const decision = restaurantSupplierDecision(rfq)
  if (decision && ARCHIVED_RESTAURANT_DECISIONS.has(decision)) {
    return true
  }
  return false
}

export const formatDate = (value: string): string => {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value))
  } catch {
    return value
  }
}

export const formatDeliveryDate = (isoDate: string): string => {
  try {
    const [year, month, day] = isoDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(date)
  } catch {
    return isoDate
  }
}

export const formatDeliveryWindow = (
  date: string | null | undefined,
  timeUntil: string | null | undefined,
): string | null => {
  if (!date) {
    return null
  }
  const formattedDate = formatDeliveryDate(date)
  if (timeUntil) {
    const time = timeUntil.length >= 5 ? timeUntil.slice(0, 5) : timeUntil
    return `Желаемая доставка: ${formattedDate} до ${time}`
  }
  return `Желаемая доставка: ${formattedDate}`
}

export const formatLineLabel = (line: RfqLineDto): string => {
  const product = line.productName?.trim()
  const sku = line.supplierSku?.trim()
  const title = product && sku ? `${product} · ${sku}` : product || sku || `Позиция #${line.id}`
  const qty = `${line.quantity} ${line.unit}`.trim()
  return line.note ? `${title} — ${qty} · ${line.note}` : `${title} — ${qty}`
}

export const isExcludedLine = (line: RfqLineDto): boolean =>
  (line.lineStatus ?? 'active').toLowerCase() === 'excluded'

export const activeLines = (lines: RfqLineDto[]): RfqLineDto[] =>
  lines.filter((line) => !isExcludedLine(line))

export const excludedLines = (lines: RfqLineDto[]): RfqLineDto[] =>
  lines.filter((line) => isExcludedLine(line))

export const restaurantDisplayName = (item: SupplierRfqInboxItemDto): string => {
  if (item.restaurantTradeName?.trim()) {
    return item.restaurantTradeName.trim()
  }
  if (item.restaurantLegalName?.trim()) {
    return item.restaurantLegalName.trim()
  }
  return item.restaurantOrganizationName
}

export const needsRestaurantAction = (rfq: RfqDto): boolean => {
  const decision = restaurantSupplierDecision(rfq)
  return decision === 'substitution_proposed' || decision === 'awaiting_customer_confirmation'
}

export const isRestaurantActionFilter = (filter: string | null): boolean => filter === 'action'

export const filterRestaurantOrdersForView = (
  orders: RfqDto[],
  actionFilter: boolean,
): RfqDto[] => (actionFilter ? orders.filter(needsRestaurantAction) : orders)
